import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule} from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { ChatService } from '../../services/chat.service';
import { DatasetService } from '../../services/dataset.service';
import { ChatMessage, ChatRequest, ChatResponse, ChatImage } from '../../models/chat.model';
import { Dataset } from '../../models/dataset.model';
import { marked } from 'marked';

@Component({
  selector: 'app-public-chat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    ChipModule,
    ProgressSpinnerModule,
    DialogModule
  ],
  templateUrl: './public-chat.html',
  styleUrl: './public-chat.scss'
})
export class PublicChat implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  chatForm: FormGroup;
  datasetId: string | null = null;
  dataset: Dataset | null = null;
  messages: ChatMessage[] = [];
  conversationStarters: string[] = [];
  isLoading = false;
  datasetNotFound = false;
  sessionId: string | null = null;
  uiVersion = '1.3.3';
  showDescriptionDialog = false;

  // Optional launch-URL tracking params (opaque encrypted blob + key id).
  // Read from the query string once on init and forwarded verbatim on every
  // chat POST; the client never inspects or decrypts them.
  euParam: string | null = null;
  kParam: string | null = null;

  // Image gallery tracking - maps message ID to images
  messageImages: Map<string, ChatImage[]> = new Map();
  selectedImageIndex = 0;
  showGalleria = false;
  activeGalleriaImages: ChatImage[] = [];

  // Session tracking constants
  private readonly SESSION_KEY = 'ask-imc-anon-session';
  private readonly SESSION_FLAG = 'ask-imc-session-active';

  private lowConfidenceMessages = [
    "I don't have enough information to provide a confident answer to that question.",
    "I'm not finding sufficient details in my knowledge base to answer that accurately.",
    "That's outside the scope of the information I have available.",
    "I don't have enough context to give you a reliable answer.",
    "I'm unable to find relevant information for that query in my current dataset."
  ];

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private chatService: ChatService,
    private datasetService: DatasetService,
    private titleService: Title
  ) {
    this.chatForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  ngOnInit(): void {
    this.initializeSession();
    this.datasetId = this.route.snapshot.paramMap.get('datasetId');
    this.euParam = this.route.snapshot.queryParamMap.get('eu');
    this.kParam = this.route.snapshot.queryParamMap.get('k');
    if (this.datasetId) {
      this.loadDataset();
    } else {
      this.datasetNotFound = true;
    }
  }

  /**
   * Initialize anonymous session ID for tracking.
   * - Generates new session ID on browser restart
   * - Persists session ID across page refreshes within same browser session
   */
  private initializeSession(): void {
    // Check if this is a new browser session
    const isNewSession = !sessionStorage.getItem(this.SESSION_FLAG);

    if (isNewSession) {
      // Generate new session ID on browser restart
      this.sessionId = crypto.randomUUID();
      localStorage.setItem(this.SESSION_KEY, this.sessionId);
      sessionStorage.setItem(this.SESSION_FLAG, 'true');
    } else {
      // Reuse existing session ID within same browser session
      this.sessionId = localStorage.getItem(this.SESSION_KEY) || crypto.randomUUID();
      localStorage.setItem(this.SESSION_KEY, this.sessionId);
    }
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  loadDataset(): void {
    if (!this.datasetId) return;

    this.datasetService.getPublicDataset(this.datasetId).subscribe({
      next: (dataset) => {
        if (!dataset.isActive) {
          this.datasetNotFound = true;
          return;
        }
        this.dataset = dataset;

        // Set page title
        const pageTitle = dataset.title || dataset.name || 'Chat';
        this.titleService.setTitle(pageTitle);

        this.conversationStarters = dataset.conversationStarters || [];
      },
      error: () => {
        this.datasetNotFound = true;
      }
    });
  }

  sendMessage(): void {
    if (this.chatForm.valid && this.datasetId && !this.isLoading) {
      const messageText = this.chatForm.get('message')?.value.trim();

      if (!messageText) return;

      // Add user message
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        conversationId: this.sessionId || '',
        messageType: 'user',
        content: messageText,
        timestamp: new Date().toISOString()
      };
      this.messages.push(userMessage);

      // Clear form and set loading
      this.chatForm.reset();
      this.isLoading = true;

      // Prepare request
      const request: ChatRequest = {
        message: messageText,
        datasetId: this.datasetId,
        sessionId: this.sessionId || undefined
      };

      // Forward optional launch-URL tracking params verbatim, only when present.
      // The client never decrypts these; the backend reads eu/k from the body.
      if (this.euParam) {
        request.eu = this.euParam;
      }
      if (this.kParam) {
        request.k = this.kParam;
      }

      // Send message
      this.chatService.sendMessage(request).subscribe({
        next: (response: ChatResponse) => {
          this.sessionId = response.sessionId;

          // Handle low confidence
          let responseText = response.message;
          if (response.confidenceScore < 0.5) {
            responseText = this.getRandomLowConfidenceMessage();
          }

          // Add assistant response
          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            conversationId: response.conversationId,
            messageType: 'assistant',
            content: responseText,
            timestamp: new Date().toISOString(),
            confidenceScore: response.confidenceScore,
            responseTimeMs: response.responseTimeMs
          };
          this.messages.push(assistantMessage);

          // Store images for this message if any were returned
          if (response.images && response.images.length > 0) {
            this.messageImages.set(assistantMessage.id, response.images);
          }

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;

          // Add error message
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            conversationId: '',
            messageType: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date().toISOString()
          };
          this.messages.push(errorMessage);
        }
      });
    }
  }

  useConversationStarter(text: string): void {
    this.chatForm.patchValue({ message: text });
    this.sendMessage();
  }

  clearConversation(): void {
    this.messages = [];
    this.sessionId = null;
    this.chatForm.reset();
  }

  getConfidenceClass(score: number): string {
    if (score >= 0.8) return 'bg-green-100 text-green-800';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }

  private getRandomLowConfidenceMessage(): string {
    const randomIndex = Math.floor(Math.random() * this.lowConfidenceMessages.length);
    return this.lowConfidenceMessages[randomIndex];
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {}
  }

  openDescriptionDialog(): void {
    this.showDescriptionDialog = true;
  }

  getFormattedDescription(): string {
    if (!this.dataset?.description) return '';
    return marked.parse(this.dataset.description) as string;
  }

  // Get images for a specific message
  getMessageImages(messageId: string): ChatImage[] {
    return this.messageImages.get(messageId) || [];
  }

  // Check if a message has images
  hasImages(messageId: string): boolean {
    return this.messageImages.has(messageId) && this.messageImages.get(messageId)!.length > 0;
  }

  // Open gallery for a specific image
  openGallery(messageId: string, index: number = 0): void {
    const images = this.messageImages.get(messageId);
    if (images && images.length > 0) {
      this.activeGalleriaImages = images;
      this.selectedImageIndex = index;
      this.showGalleria = true;
    }
  }

  // Close the gallery
  closeGallery(): void {
    this.showGalleria = false;
    this.activeGalleriaImages = [];
    this.selectedImageIndex = 0;
  }

  // Get the currently selected image
  getSelectedImage(): ChatImage | null {
    if (this.activeGalleriaImages.length > 0 && this.selectedImageIndex < this.activeGalleriaImages.length) {
      return this.activeGalleriaImages[this.selectedImageIndex];
    }
    return null;
  }

  // Navigate to previous image
  prevImage(): void {
    if (this.selectedImageIndex > 0) {
      this.selectedImageIndex--;
    } else {
      this.selectedImageIndex = this.activeGalleriaImages.length - 1;
    }
  }

  // Navigate to next image
  nextImage(): void {
    if (this.selectedImageIndex < this.activeGalleriaImages.length - 1) {
      this.selectedImageIndex++;
    } else {
      this.selectedImageIndex = 0;
    }
  }
}
